import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isSameDay } from 'date-fns'
import { Table } from '@tanstack/react-table'

import { useDateStore } from '@/components/DateInput'
import { useCombinedLogsTableStateStore } from '../store'
import { useSharedRoutesStore } from '@/modules/Workstation/store'
import { useGetAreas } from '@/modules/Areas'
import { FilterType, Log } from '@/types/logs'

import styles from '../ui/CombinedLogs.module.scss'

export const useShowSharedLog = (
    isLoading: boolean,
    table: Table<Log>,
    logContainerRef: React.RefObject<HTMLDivElement>
) => {
    const sharedLogRef = useRef<HTMLTableRowElement>(null)
    const { date, setDate } = useDateStore()
    const [searchParams] = useSearchParams()
    const { setFilter } = useCombinedLogsTableStateStore()
    const { setSharedRoutes } = useSharedRoutesStore()
    const { data: areas } = useGetAreas()

    const allRoutes = areas?.flatMap((area) => area.workstations.flatMap((workstation) => workstation.routes))

    useEffect(() => {
        if (isLoading) return

        const sharedLogId = searchParams.get('sharedLogId')
        if (!sharedLogId) return

        // Check if date is correct
        const sharedLogDate = searchParams.get('date')
        if (!sharedLogDate) return

        if (!isSameDay(new Date(sharedLogDate), date)) {
            setDate(new Date(sharedLogDate))
        }

        const sharedLogRoute = searchParams.get('route')
        if (sharedLogRoute && allRoutes) {
            setSharedRoutes(
                allRoutes
                    .filter((route) => route.route === sharedLogRoute)
                    .map((route) => ({
                        route: route.route,
                        garage: '',
                        managementCentre: '',
                        routeFrequency: '',
                        routePriority: '',
                        ewt: `${route.ewt}`,
                        mps: `${route.mps}`,
                        actual: route.actual,
                        expected: route.expected,
                        variance: `${route.variance}`,
                        isInaccurate: route.isInaccurate,
                        isOlderThan30Minutes: route.isOlderThan30Minutes,
                        isOldValue: route.isOldValue,
                    })) || []
            )
        }

        // Find the shared log in the table data
        const rows = table.getCoreRowModel().rows
        const sharedLogIndex = rows.findIndex((row) => row.original.id === sharedLogId)

        if (sharedLogIndex >= 0) {
            //set filter to all logs
            setFilter(FilterType.All)

            // Scroll to the shared log item
            setTimeout(() => {
                if (sharedLogRef.current && logContainerRef.current) {
                    // Calculate the position of the shared log item relative to the table container
                    const containerRect = logContainerRef.current.getBoundingClientRect()
                    const logItemRect = sharedLogRef.current.getBoundingClientRect()

                    // Scroll the table container to bring the shared log item into view with smooth behavior
                    logContainerRef.current.scrollTo({
                        top: sharedLogRef.current.offsetTop - containerRect.height / 2 + logItemRect.height / 2,
                        behavior: 'smooth',
                    })

                    // Highlight the shared log item temporarily
                    sharedLogRef.current.classList.add(styles.selectedRow)
                }
            }, 500) // Small delay to ensure the table is fully rendered
        }
    }, [isLoading, searchParams])

    return { sharedLogRef }
}
